import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  getFeatureFlagClient,
  useBooleanFlagValue,
} from "@worktango/feature-flags-react";

import { ThemeProvider } from "@kazoohr/confetti";

const DARK_MODE_FLAG = "dark-mode";
// Read by the pre-paint script in ui/index.html so first paint can honor the
// last-known flag value before DevCycle resolves.
export const DARK_MODE_CACHE_KEY = "constellation-dark-mode";

function readCachedFlag(): boolean {
  try {
    return localStorage.getItem(DARK_MODE_CACHE_KEY) === "true";
  } catch {
    // localStorage may be unavailable (e.g., private browsing, blocked cookies).
    return false;
  }
}

const DarkModeContext = createContext(false);

export function useDarkModeEnabled(): boolean {
  return useContext(DarkModeContext);
}

function useDarkModeFlag(): boolean {
  const flagValue = useBooleanFlagValue(DARK_MODE_FLAG, false);
  const [cachedFlag] = useState(readCachedFlag);
  const flagReady = String(getFeatureFlagClient().providerStatus) === "READY";
  return flagReady ? flagValue : cachedFlag;
}

export function ConstellationThemeProvider({ children }: PropsWithChildren) {
  const darkModeEnabled = useDarkModeFlag();
  useEffect(() => {
    try {
      localStorage.setItem(DARK_MODE_CACHE_KEY, String(darkModeEnabled));
    } catch {
      // The pre-paint script just won't see a cached value.
    }
  }, [darkModeEnabled]);
  return (
    <DarkModeContext.Provider value={darkModeEnabled}>
      <ThemeProvider enabled={darkModeEnabled}>{children}</ThemeProvider>
    </DarkModeContext.Provider>
  );
}
